import { formatAmount, resolveStorefrontFeatures } from "@loot-os/api-contract";
import { Button, buttonVariants } from "@loot-os/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@loot-os/ui/components/tabs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useRouter, useSearch } from "@tanstack/react-router";
import { ArrowRight, ExternalLink, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { BalancePacksCard } from "../components/BalancePacksCard";
import { CountryCombobox } from "../components/CountryCombobox";
import { CurrencyCard } from "../components/CurrencyCard";
import { DisputeStatusBadge } from "../components/DisputeStatusBadge";
import { PurchaseStatusBadge } from "../components/PurchaseStatusBadge";
import { SectionSwitch, StorefrontSection } from "../components/StorefrontSection";
import { StripeAccountReadiness } from "../components/StripeAccountReadiness";
import { amountColumnHeader, useCurrency } from "../lib/currency";
import { useActiveEnvironmentId } from "../lib/environment";
import { centsToDollars, formatDate } from "../lib/format";
import {
  isPaymentsTab,
  PAYMENTS_TAB_LABELS,
  type PaymentsSearch,
  type PaymentsTab,
  resolvePaymentsTab,
  visiblePaymentsTabs,
} from "../lib/paymentsTabs";
import { useStorefrontConfig, useStorefrontConfigQuery } from "../lib/storefront";
import { stripeDisputedPaymentUrl } from "../lib/stripeDashboard";
import { type trpcClient, useTRPC } from "../lib/trpc";
import { useStandingNotice } from "../lib/useStandingNotice";
import { useTenantRole } from "../lib/useTenantRole";

type StripeAccounts = Awaited<ReturnType<typeof trpcClient.payments.get.query>>;
type StripeAccountRow = NonNullable<StripeAccounts["live"]>;
type Mode = "live" | "test";

// Countries where Stripe supports connected accounts.
const STRIPE_COUNTRIES = [
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "BR", name: "Brazil" },
  { code: "BG", name: "Bulgaria" },
  { code: "CA", name: "Canada" },
  { code: "HR", name: "Croatia" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DK", name: "Denmark" },
  { code: "EE", name: "Estonia" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "DE", name: "Germany" },
  { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" },
  { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" },
  { code: "IE", name: "Ireland" },
  { code: "IT", name: "Italy" },
  { code: "JP", name: "Japan" },
  { code: "LV", name: "Latvia" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MY", name: "Malaysia" },
  { code: "MT", name: "Malta" },
  { code: "MX", name: "Mexico" },
  { code: "NL", name: "Netherlands" },
  { code: "NZ", name: "New Zealand" },
  { code: "NO", name: "Norway" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "RO", name: "Romania" },
  { code: "SG", name: "Singapore" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "ES", name: "Spain" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "TH", name: "Thailand" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
] as const;

/**
 * The per-environment balancePurchases switch, on the page header: the last one between
 * "configured" and "shoppers can buy". It sends its one key, and the server merges the patch,
 * so the battles switch stays as it is, whatever this page loaded.
 */
function BalancePurchasesSwitch() {
  const trpc = useTRPC();
  const { can } = useTenantRole();
  const { query, update } = useStorefrontConfig();
  const packs = useQuery(trpc.balancePacks.list.queryOptions());

  const features = query.data ? resolveStorefrontFeatures(query.data.config.features) : null;
  const enabled = features?.balancePurchases ?? false;
  // Enabled with nothing to sell renders an empty buy section on the storefront.
  const noActivePacks = enabled && packs.isSuccess && !packs.data.some((p) => p.active);
  useStandingNotice(noActivePacks, {
    id: "balance-packs-empty",
    message: "No active packs — shoppers have nothing to buy. Add one on the Packs tab.",
  });

  if (!features) return null;

  return (
    <SectionSwitch
      id="payments-balance-purchases"
      label="Balance purchases"
      checked={enabled}
      disabled={update.isPending || !can("editor")}
      onCheckedChange={(checked) => update.mutate({ features: { balancePurchases: checked } })}
    />
  );
}

/**
 * One mode's connected account: create form when absent, status + onboarding
 * controls when present. The merchant never sees any of this — staff drive the
 * whole Stripe lifecycle on their behalf ("white glove").
 */
function AccountCard({
  mode,
  account,
  keyConfigured,
  tenantId,
  canCreate,
  onChanged,
}: {
  mode: Mode;
  account: StripeAccountRow | null;
  keyConfigured: boolean;
  tenantId: string;
  /** Account creation is white-glove: platform admins only. */
  canCreate: boolean;
  onChanged: () => void;
}) {
  const trpc = useTRPC();
  const [showCreate, setShowCreate] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("US");

  const create = useMutation(
    trpc.platform.stripe.createAccount.mutationOptions({
      onSuccess: () => {
        setShowCreate(false);
        onChanged();
      },
    }),
  );
  // Links are single-use and expire in minutes, so one is minted per click.
  const onboard = useMutation(
    trpc.payments.createOnboardingLink.mutationOptions({
      onSuccess: ({ url }) => window.open(url, "_blank", "noopener"),
    }),
  );
  const refresh = useMutation(
    trpc.payments.refreshStatus.mutationOptions({ onSuccess: onChanged }),
  );

  return (
    <Card id="payments">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {mode === "live" ? "Live" : "Test"} account
          {account && (
            <span className="font-mono text-xs font-normal text-muted-foreground">
              {account.stripeAccountId}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {mode === "live"
            ? "Charges from the live environment's storefront; sandboxes share the test account, managed from a sandbox environment."
            : "Charges from sandbox storefronts (Stripe test mode); the live environment charges the live account, managed from there."}{" "}
          All funds settle to the platform balance (100% application fee); merchant payouts are
          handled manually outside Stripe. Disputes land on the connected account.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!account ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              No {mode}-mode connected account yet.
              {!keyConfigured && ` The stage has no ${mode}-mode platform key configured.`}
              {!canCreate && " Loot OS sets accounts up for you — contact support to get started."}
            </p>
            {canCreate && (
              <Button
                type="button"
                disabled={!keyConfigured}
                onClick={() => {
                  create.reset();
                  setShowCreate(true);
                }}
              >
                Create account
              </Button>
            )}
          </div>
        ) : (
          <>
            <StripeAccountReadiness account={account} />

            <div className="text-sm text-muted-foreground">
              <p>
                {account.businessName} · {account.email} · {account.country}
              </p>
              <p>Updated {formatDate(account.updatedAt)}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={onboard.isPending}
                onClick={() => onboard.mutate({ mode })}
              >
                <ExternalLink />
                {onboard.isPending ? "Opening…" : "Open onboarding"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate({ mode })}
              >
                <RefreshCw />
                Refresh status
              </Button>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={showCreate} onOpenChange={(open) => !open && setShowCreate(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create {mode}-mode Stripe account</DialogTitle>
            <DialogDescription>
              Creates the connected account with these pre-fill details, then complete the
              merchant's KYC through "Open onboarding" — the Stripe-hosted flow, driven by you on
              their behalf.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({
                tenantId,
                mode,
                businessName: businessName.trim(),
                email: email.trim(),
                country,
              });
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor={`stripe-business-${mode}`}>Business name</Label>
              <Input
                id={`stripe-business-${mode}`}
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Collectibles"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`stripe-email-${mode}`}>Merchant email</Label>
              <Input
                id={`stripe-email-${mode}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@acme.com"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor={`stripe-country-${mode}`}>Country</Label>
              <CountryCombobox
                countries={STRIPE_COUNTRIES}
                id={`stripe-country-${mode}`}
                value={country}
                onChange={setCountry}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={create.isPending || !businessName.trim() || !email.trim()}
              >
                {create.isPending ? "Creating…" : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * Row action opening a dispute in Stripe, where evidence is submitted.
 *
 * The mode comes from matching the row's connected account against the tenant's
 * two accounts, not from the environment being viewed: the same object id
 * doesn't exist in the other mode, so a guess links nowhere. Both halves of the
 * link can be missing — the accounts query may still be in flight, and a stage
 * without that mode's platform key has no platform account — and then the cell
 * stays empty rather than emitting a dead link.
 */
function StripeDisputeLink({
  dispute,
  accounts,
}: {
  dispute: { stripeAccountId: string; chargeId: string; paymentIntentId: string | null };
  accounts: StripeAccounts | undefined;
}) {
  const mode =
    accounts?.live?.stripeAccountId === dispute.stripeAccountId
      ? "live"
      : accounts?.test?.stripeAccountId === dispute.stripeAccountId
        ? "test"
        : null;
  const platformAccountId = mode ? accounts?.platform[mode] : null;
  if (!mode || !platformAccountId) return null;
  // The charge is the fallback ref: payment_intent is null on older charges.
  const paymentId = dispute.paymentIntentId ?? dispute.chargeId;
  return (
    <a
      href={stripeDisputedPaymentUrl({
        platformAccountId,
        stripeAccountId: dispute.stripeAccountId,
        mode,
        paymentId,
      })}
      target="_blank"
      rel="noreferrer"
      title={`Open ${paymentId} in Stripe`}
      className={buttonVariants({ variant: "ghost", size: "sm" })}
    >
      Stripe
      <ExternalLink />
    </a>
  );
}

/** The environment's balance purchases, newest first, with the revenue report a step away. */
function PurchasesCard({ tenantId }: { tenantId: string }) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const purchases = useQuery(trpc.payments.purchases.queryOptions({}));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent purchases</CardTitle>
        <CardDescription>
          Balance purchases in this environment. Refunds and disputes only flip status here — a
          credited balance is never clawed back automatically (use a ledger adjustment for that).
        </CardDescription>
        <CardAction>
          <Link
            to="/tenants/$tenantId/storefront/payments/revenue"
            params={{ tenantId }}
            className={buttonVariants({ variant: "ghost", size: "sm" })}
          >
            Revenue report
            <ArrowRight />
          </Link>
        </CardAction>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Pack</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">
                {amountColumnHeader("Credited", currency)}
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Completed</TableHead>
              <TableHead>Stripe refs</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchases.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No purchases yet.
                </TableCell>
              </TableRow>
            )}
            {purchases.data?.data.map((purchase) => (
              <TableRow key={purchase.id}>
                <TableCell className="text-muted-foreground">
                  {formatDate(purchase.createdAt)}
                </TableCell>
                <TableCell>
                  {purchase.shopperEmail ? (
                    <Link
                      to="/tenants/$tenantId/users"
                      params={{ tenantId }}
                      search={{ user: purchase.endUserId }}
                      className="underline underline-offset-2 hover:text-primary"
                    >
                      {purchase.shopperEmail}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>{purchase.packName || purchase.packId}</TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {centsToDollars(purchase.amountCents)}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {formatAmount(purchase.creditedCents, currency)}
                </TableCell>
                <TableCell>
                  <PurchaseStatusBadge status={purchase.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {purchase.completedAt ? formatDate(purchase.completedAt) : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5 font-mono text-xs text-muted-foreground">
                    <span title="Checkout session">{purchase.checkoutSessionId}</span>
                    {purchase.paymentIntentId && (
                      <span title="Payment intent">{purchase.paymentIntentId}</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/** The chargebacks against this environment's purchases, each a step from Stripe. */
function DisputesCard({
  tenantId,
  accounts,
}: {
  tenantId: string;
  accounts: StripeAccounts | undefined;
}) {
  const trpc = useTRPC();
  const disputes = useQuery(trpc.payments.disputes.queryOptions({}));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Disputes</CardTitle>
        <CardDescription>
          Chargebacks against this environment's purchases, mirrored from Stripe. A dispute debits
          the connected account (amount plus Stripe's dispute fee); evidence is submitted in the
          Stripe dashboard before the respond-by deadline.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Disputed</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Respond by</TableHead>
              <TableHead>Stripe refs</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {disputes.data?.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No disputes.
                </TableCell>
              </TableRow>
            )}
            {disputes.data?.data.map((dispute) => (
              <TableRow key={dispute.id}>
                <TableCell className="text-muted-foreground">
                  {formatDate(dispute.disputedAt)}
                </TableCell>
                <TableCell>
                  {dispute.shopperEmail ? (
                    <Link
                      to="/tenants/$tenantId/users"
                      params={{ tenantId }}
                      search={{ user: dispute.endUserId }}
                      className="underline underline-offset-2 hover:text-primary"
                    >
                      {dispute.shopperEmail}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-xs">
                  {centsToDollars(dispute.amountCents)}
                </TableCell>
                <TableCell>{dispute.reason.replaceAll("_", " ")}</TableCell>
                <TableCell>
                  <DisputeStatusBadge status={dispute.status} />
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {dispute.evidenceDueBy ? formatDate(dispute.evidenceDueBy) : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5 font-mono text-xs text-muted-foreground">
                    <span title="Dispute">{dispute.stripeDisputeId}</span>
                    {dispute.paymentIntentId && (
                      <span title="Payment intent">{dispute.paymentIntentId}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <StripeDisputeLink dispute={dispute} accounts={accounts} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * The storefront's payments, one environment at a time, a tab each: the Stripe connected
 * account behind it, the currency amounts read in, the packs shoppers buy, and the purchases and
 * disputes that followed; the switch that offers balance purchases sits on the header. The tab
 * rides `?tab=` (`paymentsTabs.ts`), so a tab is a link. The currency, the switch and the packs
 * are every member's (edits at editor); the Stripe account, the purchases, the disputes and the
 * revenue report are an admin's: their tabs are hidden below that and their queries never run.
 * A storefront section, so `StorefrontLayout` has loaded the config before this mounts.
 */
export function PaymentsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  // null = live; the switcher in the tenant header picks which env's purchases we see.
  const environmentId = useActiveEnvironmentId() ?? undefined;
  const { isPlatformAdmin, can } = useTenantRole();
  const isAdmin = can("admin");
  const search = useSearch({ strict: false }) as PaymentsSearch;
  const tabs = visiblePaymentsTabs(isAdmin);
  const activeTab = resolvePaymentsTab(search.tab, isAdmin);

  const setTab = (tab: PaymentsTab) => {
    void router.navigate({
      to: "/tenants/$tenantId/storefront/payments",
      params: { tenantId },
      search: { tab },
      replace: true,
    });
  };

  // The account is read here rather than on its tab: the disputes link into Stripe through it.
  const accounts = useQuery({ ...trpc.payments.get.queryOptions(), enabled: isAdmin });
  // The account is per (tenant, mode) but the page views one environment at a
  // time: the live env charges the live account, sandboxes the test one — so
  // only the active environment's account is shown, to match the switch, the
  // packs and the purchases beside it.
  const { query: configQuery } = useStorefrontConfigQuery();
  const activeMode = configQuery.data?.environment.type === "live" ? "live" : "test";

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: trpc.payments.get.queryOptions().queryKey,
    });
  };

  // Onboarding return (?onboarding=return&mode=…): v2 accounts don't emit
  // account.updated webhooks, so pull fresh status the moment the admin lands
  // back here from the Stripe-hosted flow, and land them on the account tab.
  const returnRefresh = useMutation(
    trpc.payments.refreshStatus.mutationOptions({ onSettled: invalidate }),
  );
  const handledReturnRef = useRef(false);
  useEffect(() => {
    if (handledReturnRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const onboarding = params.get("onboarding");
    const mode = params.get("mode");
    if (!onboarding) return;
    handledReturnRef.current = true;
    if (onboarding === "return" && (mode === "live" || mode === "test")) {
      returnRefresh.mutate({ mode });
    }
    void router.navigate({
      to: "/tenants/$tenantId/storefront/payments",
      params: { tenantId },
      search: { tab: "account" },
      replace: true,
    });
  }, [tenantId, returnRefresh, router]);

  // Narrowing only: the layout renders a section once the same query has data.
  if (!configQuery.data) return null;
  const { environment } = configQuery.data;

  return (
    <StorefrontSection
      title="Payments"
      description="The Stripe account behind this storefront, how its currency reads, the packs shoppers buy, and the purchases and disputes that followed."
      environment={environment}
      aside={<BalancePurchasesSwitch key={`balance-${environmentId ?? "live"}`} />}
    >
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (isPaymentsTab(value)) setTab(value);
        }}
        className="gap-4"
      >
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {PAYMENTS_TAB_LABELS[tab]}
            </TabsTrigger>
          ))}
        </TabsList>

        {isAdmin && (
          <TabsContent value="account">
            <AccountCard
              key={`account-${activeMode}`}
              mode={activeMode}
              account={(activeMode === "live" ? accounts.data?.live : accounts.data?.test) ?? null}
              keyConfigured={accounts.data?.configured[activeMode] ?? false}
              tenantId={tenantId}
              canCreate={isPlatformAdmin}
              onChanged={invalidate}
            />
          </TabsContent>
        )}

        {/* Kept mounted: the one panel whose unsaved edit lives outside a dialog, so a look at
            another tab does not discard a half-typed currency. */}
        <TabsContent value="currency" keepMounted>
          <CurrencyCard />
        </TabsContent>

        <TabsContent value="packs">
          <BalancePacksCard />
        </TabsContent>

        {isAdmin && (
          <>
            <TabsContent value="purchases">
              <PurchasesCard tenantId={tenantId} />
            </TabsContent>
            <TabsContent value="disputes">
              <DisputesCard tenantId={tenantId} accounts={accounts.data} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </StorefrontSection>
  );
}
